import { CircularProgress } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import {
  ArrowLeftIcon,
  Box,
  EmailIcon,
  GKButton,
  GKOtpInput,
  GKTextField,
  LockIcon,
  PhoneInput,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

// Public onboarding-auth endpoints — no localStorage token should be attached.
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

const RESEND_SECONDS = 30;

const IconBadge = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      alignItems: "center",
      bgcolor: "var(--primary-50, #e6f0f3)",
      borderRadius: "50%",
      color: "var(--primary-900)",
      display: "flex",
      height: 56,
      justifyContent: "center",
      mx: "auto",
      width: 56,
    }}
  >
    {children}
  </Box>
);

const OnboardingAuth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const onboardingId = searchParams.get("onboarding_id");
  const logo = location.search.split("logo=")[1];

  const [phone, setPhone] = useState(searchParams.get("phone") || "");
  const [countryCode, setCountryCode] = useState("+91");
  const [email, setEmail] = useState(searchParams.get("client_email") || "");

  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isDoneTyping, setIsDoneTyping] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [hasError, setHasError] = useState(false);

  const sendOtpMutation = useMutation({
    mutationKey: ["MF_ONBOARDING_SEND_OTP"],
    mutationFn: () =>
      publicApi.post(`/onboarding-auth/${onboardingId}/send-otp/`, {
        phone,
        email,
      }),
    onSuccess: () => {
      setOtpSent(true);
      setResendIn(RESEND_SECONDS);
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.message || "Could not send OTP."),
  });

  const verifyOtpMutation = useMutation({
    mutationKey: ["MF_ONBOARDING_VERIFY_OTP"],
    mutationFn: (otp: string) =>
      publicApi.post(`/onboarding-auth/${onboardingId}/verify-otp/`, {
        phone,
        email,
        otp,
      }),
    onSuccess: () => {
      // This flow issues no token — on success proceed to the onboarding flow,
      // carrying forward all the original query keys.
      navigate(`/investor/mf/onboarding${location.search}`);
    },
    onError: () => {
      toast.error("Invalid or expired OTP. Please try again.");
      setOtpCode("");
      setIsDoneTyping(false);
    },
  });

  useEffect(() => {
    if (isDoneTyping) verifyOtpMutation.mutate(otpCode);
  }, [isDoneTyping]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const handleChange = () => {
    setOtpSent(false);
    setOtpCode("");
    setIsDoneTyping(false);
    setResendIn(0);
  };

  const verifying = verifyOtpMutation.isLoading;

  return (
    <Box
      sx={{
        bgcolor: "#F8FAFC",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "var(--white)",
          borderBottom: "1px solid var(--neutral-100)",
          display: "flex",
          px: { xs: 2, sm: 4 },
          py: 2,
        }}
      >
        <img
          src={logo || "/Logos/iconFullLogo.png"}
          alt=""
          style={{ maxHeight: "32px", width: "auto" }}
          onError={({ currentTarget }) => {
            currentTarget.src = "https://gridkey.in/Logos/iconFullLogo.png";
          }}
        />
      </Box>

      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flex: 1,
          justifyContent: "center",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!otpSent) sendOtpMutation.mutate();
          }}
          sx={{
            bgcolor: "var(--white)",
            border: "1px solid #E3E8EF",
            borderRadius: "20px",
            boxShadow: "0 4px 24px rgba(16, 24, 40, 0.06)",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            maxWidth: 420,
            p: { xs: "28px 20px", sm: "36px 40px" },
            width: "100%",
          }}
        >
          <IconBadge>
            {otpSent ? (
              <EmailIcon height={26} width={26} />
            ) : (
              <LockIcon height={26} width={26} />
            )}
          </IconBadge>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              color="var(--black-900)"
              fontSize={22}
              fontWeight={700}
              sx={{ lineHeight: 1.3 }}
            >
              {otpSent ? "Enter verification code" : "Verify it's you"}
            </Typography>
            <Typography
              color="var(--neutral-500)"
              fontSize={13}
              fontWeight={500}
              sx={{ lineHeight: 1.5, mt: "8px" }}
            >
              {otpSent ? (
                <>
                  We&apos;ve sent a 4-digit code to{" "}
                  <Typography
                    component="span"
                    color="var(--black-900)"
                    fontSize={13}
                    fontWeight={600}
                  >
                    {countryCode} {phone}
                  </Typography>
                </>
              ) : (
                "Confirm your registered mobile number and email to start your onboarding."
              )}
            </Typography>
          </Box>

          <Show.When isTrue={!otpSent}>
            <PhoneInput
              inputLabel="Phone Number"
              onSelectChange={(event: any) => setCountryCode(event)}
              onTextChange={(event: any) => setPhone(event)}
              onTextValue={phone}
              requiredField
              onSelectValue={countryCode}
              hasError={hasError}
              setHasError={setHasError}
            />
            <GKTextField
              inputLabel="Email Address"
              requiredField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <GKButton
              variant="contained"
              type="submit"
              disabled={hasError || !phone || !email}
              loading={sendOtpMutation.isLoading}
              fullWidth
              size="large"
            >
              Send OTP
            </GKButton>
          </Show.When>

          <Show.When isTrue={otpSent}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Box
                sx={{
                  opacity: verifying ? 0.6 : 1,
                  pointerEvents: verifying ? "none" : "auto",
                }}
              >
                <GKOtpInput
                  value={otpCode}
                  onChange={setOtpCode}
                  setIsTyping={setIsDoneTyping}
                />
              </Box>

              <Show.When isTrue={verifying}>
                <Box
                  sx={{
                    alignItems: "center",
                    color: "var(--neutral-500)",
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={14} thickness={5} />
                  <Typography fontSize={12} fontWeight={500}>
                    Verifying...
                  </Typography>
                </Box>
              </Show.When>

              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "4px",
                  justifyContent: "center",
                }}
              >
                <Typography
                  color="var(--neutral-500)"
                  fontSize={12}
                  fontWeight={500}
                >
                  Didn&apos;t receive the code?
                </Typography>
                {resendIn > 0 ? (
                  <Typography
                    color="var(--neutral-400, #98a2b3)"
                    fontSize={12}
                    fontWeight={600}
                  >
                    Resend in {resendIn}s
                  </Typography>
                ) : (
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => sendOtpMutation.mutate()}
                    color="var(--primary-900)"
                    fontSize={12}
                    fontWeight={600}
                    sx={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      p: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Resend OTP
                  </Typography>
                )}
              </Box>

              <GKButton
                variant="text"
                color="dark"
                startIcon={<ArrowLeftIcon height={14} width={14} />}
                onClick={handleChange}
                disabled={verifying}
                sx={{
                  alignSelf: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Change details
              </GKButton>
            </Box>
          </Show.When>
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid #E3E8EF",
          display: "flex",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          © {dayjs().get("year")} gridkey. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default OnboardingAuth;
